"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { EMPTY_PROGRESS, filterPuzzles, PUZZLE_STORAGE_KEY, readPuzzleProgress, recordPuzzleResult, type Puzzle, type PuzzleProgress } from "@/lib/puzzles";
import { PuzzleLibrary, PuzzleLibraryIntro } from "./PuzzleLibrary";

const PuzzleTrainer = dynamic(() => import("./PuzzleTrainer").then((m) => m.PuzzleTrainer), { loading: () => <PuzzleLoading/> });
export function PuzzleLoading() {
  // The prerendered page shows this state, so it carries the page heading until the library (with its own h1) loads.
  return <div className="puzzle-page-loading" role="status"><h1 className="sr-only">Chess puzzles</h1><span className="puzzle-loading-symbol" aria-hidden>♞</span><p>Opening the puzzle collection…</p></div>;
}
/**
 * The prerendered /puzzles page: the library's heading paints straight away (it is the
 * largest text on screen) while the collection loads. The query string is unknown at build
 * time, so a deep link to one puzzle (?id=) hides the heading before first paint.
 */
export function PuzzleLibraryShell() {
  return <div className="puzzle-library-shell">
    <script dangerouslySetInnerHTML={{ __html: `if(/[?&]id=/.test(location.search))document.documentElement.dataset.puzzleLink=""` }}/>
    <header className="puzzle-page-heading"><PuzzleLibraryIntro/></header>
    <div className="puzzle-page-loading" role="status"><span className="puzzle-loading-symbol" aria-hidden>♞</span><p>Opening the puzzle collection…</p></div>
  </div>;
}
export function PuzzleExperience() {
  const params = useSearchParams();
  const id = params.get("id");
  const [puzzles, setPuzzles] = useState<Puzzle[] | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [progress, setProgress] = useState<PuzzleProgress>(EMPTY_PROGRESS);
  const progressRef = useRef(progress);
  // The shell's deep-link flag has done its job once React renders the real state.
  useEffect(() => { delete document.documentElement.dataset.puzzleLink; }, []);
  useEffect(() => {
    const controller = new AbortController();
    try { progressRef.current = readPuzzleProgress(JSON.parse(localStorage.getItem(PUZZLE_STORAGE_KEY) ?? "null")); setProgress(progressRef.current); } catch { /* Storage is optional. */ }
    fetch("/data/puzzles.json", { signal: controller.signal })
      .then((r) => { if (!r.ok) throw new Error("Puzzles unavailable"); return r.json(); })
      .then((data: Puzzle[]) => { if (!controller.signal.aborted) { if (!Array.isArray(data)) throw new Error("Invalid puzzle data"); setPuzzles(data); setError(false); } })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    const sync = (event: StorageEvent) => {
      if (event.key !== PUZZLE_STORAGE_KEY) return;
      try { progressRef.current = readPuzzleProgress(JSON.parse(event.newValue ?? "null")); setProgress(progressRef.current); } catch { /* Ignore corrupt storage. */ }
    };
    window.addEventListener("storage", sync);
    return () => { controller.abort(); window.removeEventListener("storage", sync); };
  }, [retry]);
  const result = (puzzle: Puzzle, won: boolean, assisted: boolean) => {
    const before = progressRef.current;
    const next = recordPuzzleResult(before, puzzle, won, assisted);
    progressRef.current = next; setProgress(next);
    try { localStorage.setItem(PUZZLE_STORAGE_KEY, JSON.stringify(next)); } catch { /* Progress stays in memory when storage is unavailable. */ }
    return next.rating - before.rating;
  };
  if (error) return <div className="puzzle-empty"><h1>The collection couldn’t load.</h1><p>Please try again to bring the boards back.</p><button className="puzzle-primary" onClick={() => { setError(false); setRetry((n) => n + 1); }}>Try again</button></div>;
  if (!puzzles) return id ? <PuzzleLoading/> : <PuzzleLibraryShell/>;
  if (!id) return <PuzzleLibrary puzzles={puzzles} progress={progress}/>;
  const puzzle = puzzles.find((p) => p.id === id);
  const back = new URLSearchParams(params); back.delete("id");
  const backHref = `/puzzles${back.size ? `?${back}` : ""}`;
  if (!puzzle) return <div className="puzzle-empty"><h1>Puzzle not found.</h1><p>This puzzle isn’t in the collection. There are {puzzles.length.toLocaleString()} others to explore.</p><Link className="puzzle-primary" href={backHref}>Back to the library →</Link></div>;
  const pool = filterPuzzles(puzzles, { query: params.get("q") ?? "", theme: params.get("theme") ?? "", level: params.get("level") ?? "", status: params.get("status") ?? "", sort: params.get("sort") ?? "recommended" }, progress).filter((p) => p.id !== id);
  const nextPuzzle = pool.find((p) => !progress.seen.includes(p.id)) ?? pool[0];
  const next = new URLSearchParams(params); if (nextPuzzle) next.set("id", nextPuzzle.id);
  return <PuzzleTrainer key={puzzle.id} puzzle={puzzle} progress={progress} onResult={result} backHref={backHref} nextHref={nextPuzzle ? `/puzzles?${next}` : null}/>;
}
